/**
 * Centralized API Client with Interceptors
 * Handles authentication, error handling, and retry logic
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

// Constants
const API_TIMEOUT = 120000; // 120 seconds
const MAX_RETRIES = 3;

/**
 * API Error interface
 */
export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  data?: any;
}

/**
 * API Response interface
 */
export interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
  error?: string;
}

/**
 * Create Axios instance with default configuration
 */
export const apiClient: AxiosInstance = axios.create({
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - Add authentication token
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = token;
    }
    
    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handle errors globally
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Response] ${response.config.url}`, response.data);
    }
    
    return response;
  },
  (error: AxiosError) => {
    // Handle different error scenarios
    if (error.response) {
      const { status, data } = error.response;
      
      // Unauthorized - redirect to login
      if (status === 401) {
        console.error('[API] Unauthorized - redirecting to login');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      
      // Forbidden — treat as force-logout (token blacklisted by admin)
      if (status === 403) {
        console.error('[API] Forbidden - session revoked, redirecting to login');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      
      // Server error
      if (status >= 500) {
        console.error('[API] Server error:', status);
      }
      
      // Log error in development
      if (process.env.NODE_ENV === 'development') {
        console.error(`[API Error] ${error.config?.url}`, data);
      }
    } else if (error.request) {
      // Network error
      console.error('[API] Network error - no response received');
    } else {
      // Request setup error
      console.error('[API] Request error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

/**
 * Handle API errors consistently
 */
export const handleApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>;
    
    return {
      message: axiosError.response?.data?.error || 
               axiosError.response?.data?.message || 
               axiosError.message || 
               'An error occurred',
      status: axiosError.response?.status,
      code: axiosError.code,
      data: axiosError.response?.data,
    };
  }
  
  if (error instanceof Error) {
    return {
      message: error.message,
    };
  }
  
  return {
    message: 'An unknown error occurred',
  };
};

/**
 * Retry failed requests
 */
export const retryRequest = async <T = any>(
  requestFn: () => Promise<T>,
  retries = MAX_RETRIES,
  delay = 1000
): Promise<T> => {
  try {
    return await requestFn();
  } catch (error) {
    if (retries === 0) {
      throw error;
    }
    
    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, delay));
    
    // Retry with exponential backoff
    return retryRequest(requestFn, retries - 1, delay * 2);
  }
};

/**
 * Create cancellable request
 */
export const createCancellableRequest = () => {
  const source = axios.CancelToken.source();
  
  return {
    token: source.token,
    cancel: (message?: string) => source.cancel(message),
  };
};

/**
 * Check if error is a cancellation
 */
export const isCancelError = (error: any): boolean => {
  return axios.isCancel(error);
};

export default apiClient;
